import { hashQueryKey } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';

import {
  useCancelValidateDxfSceneMapping,
  useValidateDxfSceneMapping,
} from '@frontend/api';
import { hashString } from '@frontend/utils';

import {
  EMPTY_MAPPING_VALIDATION_RESULT,
  INITIAL_MAPPING_VALIDATION_STATE,
} from 'modules/common/viewer/dxfViewer/session/mappingValidation';
import type { DxfViewerSession } from 'modules/common/viewer/dxfViewer/session/types';
import { useReadableStore } from 'modules/common/viewer/dxfViewer/session/useSessionState';

import { useDebouncedMappingDraft } from './useDebouncedMappingDraft';

/** Quiet period after the last edit before a check is dispatched. */
const VALIDATION_DEBOUNCE_MS = 500;
/** Longest the panel can go without an update while editing continues. */
const VALIDATION_MAX_WAIT_MS = 5000;

/**
 * Runs the live mapping check and mirrors it onto `session.mappingValidation`
 */
export const useDxfMappingValidation = (session: DxfViewerSession): void => {
  const mode = useReadableStore(session.mode);
  const partFileUuid = useReadableStore(session.partFileUuid) ?? '';
  const mapping = useReadableStore(session.mapping);

  const isMapping = mode === 'mapping' && Boolean(partFileUuid);
  const draft = isMapping ? mapping.draft : null;
  const { assignments, isDraftInFlight } = useDebouncedMappingDraft(
    draft,
    VALIDATION_DEBOUNCE_MS,
    VALIDATION_MAX_WAIT_MS,
  );

  // `hashQueryKey` orders the mapping payload so the order of keys is deterministic.
  // `hashString` reduces that to a small enough string to use as a react query key.
  const draftKey = useMemo(
    () => (assignments ? hashString(hashQueryKey([assignments])) : ''),
    [assignments],
  );

  const enabled = isMapping && assignments !== null;
  const cancelValidation = useCancelValidateDxfSceneMapping(partFileUuid);

  const { data, error, isError, isFetching } = useValidateDxfSceneMapping(
    { partFileUuid, draftKey, assignments: assignments ?? {} },
    {
      queryOptions: {
        // A given draft validates the same way every time, so nothing here should
        // ever refetch on its own. Superseded drafts are kept briefly so that
        // undo/redo across recent edits resolves from cache.
        staleTime: Infinity,
        cacheTime: 60_000,
        // Not just `partFileUuid`: outside mapping mode there is no draft, and
        // an empty-assignment check would be both wrong and wasteful.
        enabled,
        // `DxfIssuesPanel` needs the last settled issues to stay put while a
        // live re-check runs - only `isPending` should flip.
        keepPreviousData: true,
      },
    },
  );

  // An edit that the debounce has not dispatched yet is still "pending": the
  // issues on screen describe the previous draft, so Save must stay blocked.
  // `isFetching` alone would briefly report clean-and-settled in that window.
  const isPending = isFetching || isDraftInFlight;

  useEffect(() => {
    if (!enabled) {
      session.mappingValidation.set(INITIAL_MAPPING_VALIDATION_STATE);
      return;
    }

    session.mappingValidation.set({
      // On error `data` is undefined and the empty result reads as clean, so
      // `error` below is what actually keeps Save blocked.
      ...(data ?? EMPTY_MAPPING_VALIDATION_RESULT),
      isPending,
      isInitialized: data !== undefined || isError,
      error: isError ? error : null,
    });
  }, [enabled, data, isPending, isError, error, session]);

  // The observed query only changes when the debounced draft does, so an edit
  // that has not settled yet leaves the previous check in flight. Abort that
  // check as soon as the live draft moves; the replacement starts once the
  // quiet period elapses. Unmount still cancels through the query signal.
  useEffect(() => {
    if (enabled && !isDraftInFlight) {
      return;
    }
    cancelValidation();
  }, [enabled, draft, isDraftInFlight, cancelValidation]);
};
